<template>
  <div
    class="product-carousel critical-component-clear"
    :class="{
      'has-top-border': liquid.hasTopBorder,
      'has-collection-info': liquid.hasCollectionInfo,
      'hide-product-details': liquid.hideProductDetails,
    }"
  >
    <div
      :class="liquid.hideProductDetails
        ? 'product-carousel__info-wrapper-hide'
        : {
          'product-carousel__info-wrapper': true,
          'padding-left-desktop': !liquid.hasCollectionInfo
        }"
    >
      <div class="product-carousel__info">
        <div
          :class="liquid.hideProductDetails
            ?'product-carousel__info-inner-hide'
            : 'product-carousel__info-inner'"
        >
          <h2
            v-if="liquid.hasCollectionTitle && title"
            v-show="products.length > 0"
            :id="`section-title-${liquid.sectionId}`"
            class="product-carousel__title"
            :class="{
              'h4': liquid.hasCollectionInfo,
              'h3': !liquid.hasCollectionInfo
            }"
            v-text="title"
          />

          <template v-if="liquid.hasCollectionInfo">
            <p
              v-if="!isMobile"
              class="product-carousel__description body-2"
              v-text="collection.description"
            />
          </template>

          <btn
            v-if="collection.url"
            custom-element="a"
            :show-icon="false"
            class="product-carousel__link"
            :label="$string('collection.view_all')"
            modifiers="text"
            :url="collection.url"
          />
        </div>
      </div>
    </div>

    <div
      :class="liquid.hideProductDetails
        ? 'product-carousel__navigation-overlay'
        : 'product-carousel__navigation'"
    >
      <div
        ref="progressBar"
        :class="liquid.hideProductDetails
          ? 'product-carousel__progress-hide'
          : 'product-carousel__progress'"
      />

      <button
        ref="prevButton"
        class="
          product-carousel__button
          product-carousel__button-prev
          button
          button--nav-arrow
          button--text
        "
        type="button"
        :aria-label="$string('accessibility.carousel.next')"
        modifiers="text"
      >
        <span
          class="button__label visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />

        <span
          class="button__icon"
        >
          <icon-chevron-right />
        </span>
      </button>

      <button
        ref="nextButton"
        class="
          product-carousel__button
          button
          button--nav-arrow
          button--text
        "
        type="button"
        :aria-label="$string('accessibility.carousel.next')"
        modifiers="text"
      >
        <span
          class="button__label visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />

        <span
          class="button__icon"
        >
          <icon-chevron-right />
        </span>
      </button>
    </div>

    <template v-if="ready">
      <swiper
        class="product-carousel__content"
        :slides-per-view="swiperOptions.slidesPerView"
        :space-between="swiperOptions.spaceBetween"
        :loop="swiperOptions.loop"
        :breakpoints="swiperOptions.breakpoints"
        a11y
        :modules="modules"
        :navigation="{
          nextEl: $refs.nextButton,
          prevEl: $refs.prevButton,
        }"
        :pagination="{
          el: $refs.progressBar,
          type: 'progressbar',
        }"
        @swiper="onSwiper"
      >
        <swiper-slide
          v-for="(product, index) of products"
          :key="`product-card-${liquid.sectionId}-${product.id}`"
        >
          <product-card
            :index="index"
            :product="product"
            :hide-product-details="liquid.hideProductDetails"
            has-video
            class="product-carousel__product-card"
            @product-card-quick-add-open="disableTouch"
            @product-card-quick-add-close="enableTouch"
          />
        </swiper-slide>
      </swiper>
    </template>

    <template v-else>
      <div class="product-carousel__content" />
    </template>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Carousel with product cards.
 *
 * @param {Object} liquid.collection - Liquid collection.
 * @param {Boolean} liquid.hasCollectionInfo - Based on source & setting.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.source - 'collection' or 'recommended' from settings.
 * @param {Number} liquid.productLimit - Product limit set in section settings.
 * @param {Boolean} liquid.hasTopBorder - Based on section setting.
 * @param {Boolean} liquid.hideProductDetails - Based on section setting.
 * @param {Object} collection - only present if data source is Collection.
 * @param {String} productId - only present if data source is Recommended.
 *
 */

import { mapState } from 'vuex'

import collection from '~/graphql/queries/collection.gql'
import productRecommendations from '~/graphql/queries/productRecommendations.gql'
import { formatCollection, formatProduct } from '~/helpers/format'

import { A11y, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

import ProductCard from '~async/product-card/product-card'
import Btn from '~global/btn/btn'
import IconChevronRight from '~icons/direction/chevron/right.svg'

export default {
  name: 'ProductCarousel',

  components: {
    Btn,
    IconChevronRight,
    ProductCard,
    Swiper,
    SwiperSlide,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        collection: {},
        hasCollectionInfo: false,
        hasCollectionTitle: true,
        sectionId: '',
        source: '',
        productLimit: 9,
        hasTopBorder: true,
        hideProductDetails: false,
      }),
    },
    collection: {
      type: Object,
      default: () => ({}),
    },
    productId: {
      type: Number,
      default: 0,
    },
  },

  setup() {
    return {
      modules: [
        A11y,
        Navigation,
        Pagination,
      ],
    }
  },

  data() {
    return {
      ready: false,
      products: [],
      swiper: null,
      swiperOptions: {
        autoHeight: true,
        slidesPerView: 1.95,
        spaceBetween: 8,
        loop: true,
        breakpoints: {
          1024: {
            slidesPerView: this.liquid.hasCollectionInfo ? 2.83 : 3.83,
          },
        },
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute title to display based on  and source.
     * @returns {String}
     */
    title() {
      if (this.liquid.source !== 'collection') {
        return this.$string('product.extra.recommended_products_title')
      }

      if (this.liquid.hasCollectionTitle && this.collection.title) {
        return this.collection.title
      }

      return this.$string('search.recommended_products_title')
    },
  },

  mounted() {
    this.getProducts()
  },

  methods: {

    /**
     * Set reference for Swiper carousel.
     */
    onSwiper(swiper) {
      this.swiper = swiper
    },

    /**
     * Disable swiper touch.
     */
    disableTouch() {
      this.swiper.allowTouchMove = false
    },

    /**
     * Enable swiper touch.
     */
    enableTouch() {
      this.swiper.allowTouchMove = true
    },

    /**
     * Get collection or recommended products, based on layout.
     */
    getProducts() {
      switch (this.liquid.source) {
        case 'collection':
          this.getCollectionProducts(this.collection.handle)
          break
        case 'recommended':
          this.getRecommendedProducts(this.productId)
          break
        default:
          // eslint-disable-next-line no-console
          console.error('Failed to load products. No source specified.')
      }
    },

    /**
     * Query collection and format response.
     * @param {String} handle - Collection handle to load.
     * @param {Number} first - How many products to load.
     * @returns {Array}
     */
    async getCollectionProducts(handle, first = this.liquid.productLimit) {
      try {
        const response = await cnvs.Query({
          query: collection,
          variables: {
            country: cnvs.store.country,
            first,
            handle,
            language: cnvs.store.language,
          },
        })

        const collectionFormatted = formatCollection(response)

        this.products = collectionFormatted.products
        this.ready = true

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },

    /**
     * Make API call to Shopify Product Recommendations.
     * @param {Number} productId
     */
    async getRecommendedProducts(productId) {
      const id = `gid://shopify/Product/${productId}`

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productRecommendations,
          variables: {
            country: cnvs.store.country,
            id,
          },
        })

        let limit = this.liquid.productLimit

        if (limit > response.productRecommendations.length) {
          limit = response.productRecommendations.length
        }

        for (let index = 0; index < limit; index++) {
          const formattedProduct = formatProduct(
            { product: response.productRecommendations[index] },
          )
          this.products.push(formattedProduct)
        }

        this.ready = true

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
  },
}
</script>

<style lang="scss">
  @import './product-carousel';
</style>
